import {
  Badge,
  BookOpenText,
  CalendarIcon,
  Captions,
  Clock,
  ListMinus,
  MapPin,
  Plus,
  Repeat,
  ScrollText,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { Input } from '../../components/ui/input.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Label } from '../../components/ui/label.tsx';
import { Switch } from '../../components/ui/switch.tsx';
import { cn } from '../../lib/utils.ts';
import { format } from 'date-fns';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectValue,
  SelectTrigger,
} from '../../components/ui/select.tsx';
import { Textarea } from '../../components/ui/textarea.tsx';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs.tsx';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '../../components/ui/avatar.tsx';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover.tsx';
import { Calendar } from '../../components/ui/calendar.tsx';
import { Checkbox } from '../../components/ui/checkbox.tsx';
import { useEffect, useMemo, useState } from 'react';
import { useGetParents } from '../../api/parent/parentQueries.js';
import { useGetAllStaff } from '../../api/staff/staffQueries.js';
import { useGetChildren } from '../../api/student/studentQueries.js';
import {
  useGetClassEnrollments,
  useGetEnrollmentGroups,
} from '../../api/enrollment/enrollmentQueries.js';
import { useGetAllSessions } from '../../api/session/sessionQueries.js';
import { LoadingSpinner } from '../ui/loading-spinner.tsx';
import { useGetAllClasses } from '../../api/class/classQueries.js';

const days = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

// Still working on this
export function EventModal({ isOpen, onClose, event, onSave, onDelete }) {
  const [formData, setFormData] = useState({
    eventTitle: '',
    eventDate: new Date(),
    startTime: new Date(),
    endTime: new Date(),
    recurring: false,
    recurrenceRule: '',
    location: '',
    description: '',
    eventType: 'Event',
    participants: [],
    assignee: '',
    classEvent: false,
    classId: null,
    subjectId: null,
    termId: null,
    sessionId: null,
  });

  const [participants, setParticipants] = useState({
    students: [],
    staff: [],
    parents: [],
    groups: [],
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('students');
  const [isParticipantsOpen, setIsParticipantsOpen] = useState(false);
  const {
    data: parents,
    isLoading: isParentsLoading,
    isError: isParentsError,
  } = useGetParents();
  const {
    data: staff,
    isLoading: isStaffLoading,
    isError: isStaffError,
  } = useGetAllStaff();
  const {
    data: children,
    isLoading: isChildrenLoading,
    isError: isChildrenError,
  } = useGetChildren();
  const {
    data: enrollmentGroups,
    isLoading: isEnrollmentGroupsLoading,
    isError: isEnrollmentGroupsError,
  } = useGetEnrollmentGroups();
  const [isLoading, setIsLoading] = useState(isChildrenLoading);
  const [isError, setIsError] = useState(isChildrenError);
  const {
    data: sessions,
    isLoading: isSessionsLoading,
    isError: isSessionsError,
  } = useGetAllSessions();
  const {
    data: classes,
    isLoading: isClassesLoading,
    isError: isClassesError,
  } = useGetAllClasses();
  const {
    data: enrollments,
    isLoading: isEnrollmentLoading,
    isError: isEnrollmentError,
    refetch: refetchEnrollments,
  } = useGetClassEnrollments(formData.classId);

  const subjectList = useMemo(() => {
    if (formData.classId && !isEnrollmentLoading) {
      const uniqueSubjectsMap = new Map();
      enrollments.data.forEach((enrollment) => {
        const subjectId = enrollment.subject.id;
        uniqueSubjectsMap.set(subjectId, {
          id: subjectId,
          subjectName: enrollment.subject.subjectName,
        });
      });
      return Array.from(uniqueSubjectsMap.values());
    }
    return [];
  }, [formData, isEnrollmentLoading, enrollments]);

  useEffect(() => {
    if (event) {
      setFormData({
        eventTitle: event.eventTitle || '',
        eventDate: event.eventDate ? new Date(event.eventDate) : new Date(),
        startTime: event.startTime ? new Date(event.startTime) : new Date(),
        endTime: event.endTime ? new Date(event.endTime) : new Date(),
        recurring: event.recurring || false,
        recurrenceRule: event.recurrenceRule || '',
        location: event.location || '',
        description: event.description || '',
        eventType: event.eventType || 'Event',
        participants: event.participants || [],
        assignee: event.assignee || '',
        classEvent: event.classEvent || false,
        classId: event.classId || null,
        subjectId: event.subjectId || null,
        termId: event.termId || null,
        sessionId: event.sessionId || null,
      });
    } else {
      setFormData({
        eventTitle: '',
        eventDate: new Date(),
        startTime: new Date(),
        endTime: new Date(),
        recurring: false,
        recurrenceRule: '',
        location: '',
        description: '',
        eventType: 'Event',
        participants: [],
        assignee: '',
        classEvent: false,
        classId: null,
        subjectId: null,
        termId: null,
        sessionId: null,
      });
    }
  }, [event]);

  useEffect(() => {
    const participantObject = {
      students: children,
      staff,
      parents,
      groups: enrollmentGroups,
    };
    setParticipants(participantObject);
  }, [children, parents, staff, enrollmentGroups]);

  useEffect(() => {
    if (activeTab === 'students') {
      setIsLoading(isChildrenLoading);
      setIsError(isChildrenError);
    } else if (activeTab === 'staff') {
      setIsLoading(isStaffLoading);
      setIsError(isStaffError);
    } else if (activeTab === 'parents') {
      setIsLoading(isParentsLoading);
      setIsError(isParentsError);
    } else {
      setIsLoading(isEnrollmentGroupsLoading);
      setIsError(isEnrollmentGroupsError);
    }
  }, [
    activeTab,
    isChildrenError,
    isChildrenLoading,
    isParentsLoading,
    isParentsError,
    isStaffLoading,
    isStaffError,
    isEnrollmentGroupsError,
    isEnrollmentGroupsLoading,
  ]);

  const recurringOptions = [
    { value: 'FREQ=DAILY', label: 'Daily' },
    { value: 'FREQ=WEEKLY', label: 'Weekly' },
    { value: 'FREQ=WEEKLY;BYDAY=MO,WE,FR', label: 'Mon, Wed, Fri' },
    { value: 'FREQ=WEEKLY;BYDAY=TU,TH', label: 'Tue, Thu' },
    { value: 'FREQ=MONTHLY', label: 'Monthly' },
  ];

  const eventTypes = [
    { value: 'Schedule', label: 'Schedule' },
    { value: 'Task', label: 'Task' },
    { value: 'Event', label: 'Event' },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();

    // Format the data exactly as the API expects
    const formattedData = {
      eventTitle: formData.eventTitle,
      eventDate: formData.eventDate.toISOString(),
      startTime: formData.startTime.toISOString(),
      endTime: formData.endTime.toISOString(),
      eventType: formData.eventType,
      recurring: formData.recurring,
      recurrenceRule: formData.recurring ? formData.recurrenceRule : '',
      location: formData.location,
      description: formData.description || 'No description provided',
      participants: formData.participants || [],
      assignee: formData.assignee || '',
      classEvent: formData.classEvent || false,
      classId: formData.classId || null,
      subjectId: formData.subjectId || null,
      termId: formData.termId || null,
      sessionId: formData.sessionId || null,
    };

    // If editing, pass the ID
    if (event?.id) {
      formattedData.id = event.id;
    }

    if (formattedData.recurrenceRule === 'FREQ=WEEKLY') {
      const day = days[formData.eventDate.getDay()];
      const newValue = `${formattedData.recurrenceRule};BYDAY=${day}`;
      formattedData.recurrenceRule = newValue;
    }

    onSave(formattedData);
    onClose();
  };

  function isSubset(subset, superset) {
    const superSet = new Set(superset);
    return subset.every((item) => superSet.has(item));
  }

  function isGroupSubset(participant) {
    const onlyIdArray = participant.participants.map((p) => p.id);
    const formDataOnlyIdArray = formData.participants.map((p) => p.id);

    return isSubset(onlyIdArray, formDataOnlyIdArray);
  }

  const addParticipant = (participant) => {
    // Check if participant is already added
    if (activeTab === 'groups') {
      const onlyIdArray = participant.participants.map((p) => p.id);
      const formDataOnlyIdArray = formData.participants.map((p) => p.id);

      if (!isSubset(onlyIdArray, formDataOnlyIdArray)) {
        setFormData({
          ...formData,
          participants: [...formData.participants, ...participant.participants],
        });
      }
    } else {
      if (!formData.participants.some((p) => p.id === participant.id)) {
        setFormData({
          ...formData,
          participants: [
            ...formData.participants,
            {
              ...participant,
              type: activeTab, // Add the type (student, staff, parent)
            },
          ],
        });
      }
    }
  };

  const removeParticipantGroup = (participant) => {
    const onlyIdArray = participant.participants.map((p) => p.id);
    setFormData({
      ...formData,
      participants: formData.participants.filter(
        (p) => !onlyIdArray.includes(p.id)
      ),
    });
  };

  const removeParticipant = (participantId) => {
    setFormData({
      ...formData,
      participants: formData.participants.filter((p) => p.id !== participantId),
    });
  };

  // Filter participants based on search term
  const filteredParticipants = searchTerm
    ? participants[activeTab].filter(
        (p) =>
          p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.email.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : participants[activeTab];

  return (
    <div
      className={`fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 ${
        isOpen ? 'block' : 'hidden'
      }`}
    >
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
        <div className=" mb-4 flex flex-row justify-between items-center px-6 py-2 pt-4">
          <h2 className="text-xl font-semibold ">
            {event ? 'Edit Event' : 'Create Event'}
          </h2>
          <div className="self-center  ">
            <X
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 cursor-pointer block"
            />
          </div>
        </div>
        <form onSubmit={handleSubmit} className="h-[70vh] overflow-scroll px-6">
          <div className="mb-4">
            <Label htmlFor="eventTitle">
              <Captions className="h-4 w-4 inline-block mr-1" />
              Event Title
            </Label>
            <Input
              id="eventTitle"
              type="text"
              placeholder="Enter event title"
              required
              value={formData.eventTitle}
              onChange={(e) =>
                setFormData({ ...formData, eventTitle: e.target.value })
              }
            />
          </div>

          {/* Event Type */}
          <div className="mb-4">
            <Label htmlFor="eventType">
              <ListMinus className="h-4 w-4 inline-block mr-1" />
              Event Type
            </Label>
            <Select
              value={formData.eventType}
              onValueChange={(value) => {
                setFormData({
                  ...formData,
                  eventType: value,
                  assignee: '', // Reset assignee when changing event type
                });
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select Event Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Select Event Type</SelectLabel>
                  {eventTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          {/* Assignee */}
          {['Schedule', 'Task'].includes(formData.eventType) && (
            <>
              <div className="mb-4">
                <Label htmlFor="assignee">
                  <ListMinus className="h-4 w-4 inline-block mr-1" />
                  Assignee
                </Label>
                <Select
                  value={formData.assignee}
                  onValueChange={(value) =>
                    setFormData({ ...formData, assignee: value })
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select Assignee" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Select Assignee</SelectLabel>
                      {staff.map((assignee) => (
                        <SelectItem
                          key={assignee.staffId}
                          value={assignee.staffId}
                        >
                          {assignee.firstName} {assignee.lastName}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between mb-4 mt-4">
                <Label htmlFor="classEvent">
                  <BookOpenText className="h-4 w-4 inline-block mr-1" />
                  Class Event
                </Label>
                <Switch
                  checked={formData.classEvent}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setFormData({
                        ...formData,
                        classEvent: checked,
                      });
                    } else {
                      setFormData({
                        ...formData,
                        classEvent: checked,
                        classId: null,
                        subjectId: null,
                        termId: null,
                        sessionId: null,
                      });
                    }
                  }}
                  className="data-[state=checked]:bg-[#7C3AED]"
                />
              </div>

              {formData.classEvent && isSessionsLoading ? (
                <div className="flex justify-center">
                  <LoadingSpinner />
                </div>
              ) : (
                <div>
                  {isSessionsError ? (
                    <p className="text-red-600 bg-red-200 p-1 mb-4">
                      There was an error loading sessions
                    </p>
                  ) : (
                    <div>
                      {formData.classEvent ? (
                        <div className="pl-7">
                          <div className="mb-4">
                            <Label
                              htmlFor="session"
                              className="text-sm font-medium text-gray-700"
                            >
                              Session
                            </Label>
                            <Select
                              value={
                                formData.sessionId == null
                                  ? ''
                                  : formData.sessionId
                              }
                              onValueChange={(value) =>
                                setFormData({ ...formData, sessionId: value })
                              }
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select Session" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectGroup>
                                  <SelectLabel>Select Session</SelectLabel>
                                  {sessions.data.map((session) => (
                                    <SelectItem
                                      key={session.sessionId}
                                      value={session.sessionId}
                                    >
                                      {session.name}
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="mb-4">
                            <Label
                              htmlFor="term"
                              className="text-sm font-medium text-gray-700"
                            >
                              Term
                            </Label>
                            <Select
                              value={
                                formData.termId == null ? '' : formData.termId
                              }
                              onValueChange={(value) =>
                                setFormData({ ...formData, termId: value })
                              }
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select Term" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectGroup>
                                  <SelectLabel>Select Term</SelectLabel>
                                  {formData.sessionId !== null ? (
                                    sessions.data
                                      .find(
                                        (s) =>
                                          s.sessionId === formData.sessionId
                                      )
                                      ?.terms.map((term) => (
                                        <SelectItem
                                          key={term.termId}
                                          value={term.termId}
                                        >
                                          {term.name}
                                        </SelectItem>
                                      ))
                                  ) : (
                                    <p className="px-6 py-1 bg-amber-200 text-amber-950">
                                      Select Session First
                                    </p>
                                  )}
                                </SelectGroup>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="mb-4">
                            <Label
                              htmlFor="class"
                              className="text-sm font-medium text-gray-700"
                            >
                              Class
                            </Label>
                            <Select
                              value={
                                formData.classId == null ? '' : formData.classId
                              }
                              onValueChange={(value) =>
                                setFormData({ ...formData, classId: value })
                              }
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select Class" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectGroup>
                                  <SelectLabel>Select Class</SelectLabel>
                                  {isClassesLoading ? (
                                    <p>Loading...</p>
                                  ) : isClassesError ? (
                                    <p className="text-red-600">
                                      Error loading classes
                                    </p>
                                  ) : (
                                    <>
                                      {classes.data.map((c) => (
                                        <SelectItem
                                          key={c.classId}
                                          value={c.classId}
                                        >
                                          {c.className}
                                        </SelectItem>
                                      ))}
                                    </>
                                  )}
                                </SelectGroup>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="mb-4">
                            <Label
                              htmlFor="subject"
                              className="text-sm font-medium text-gray-700"
                            >
                              Subject
                            </Label>
                            <Select
                              value={
                                formData.subjectId == null
                                  ? ''
                                  : formData.subjectId
                              }
                              onValueChange={(value) =>
                                setFormData({ ...formData, subjectId: value })
                              }
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select Subject" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectGroup>
                                  <SelectLabel>Select Subject</SelectLabel>
                                  {subjectList.length === 0 &&
                                  formData.classId == null ? (
                                    <p className="px-6 py-1 bg-amber-200 text-amber-950">
                                      Please select a class first
                                    </p>
                                  ) : (
                                    <>
                                      {subjectList.map((subject) => (
                                        <SelectItem
                                          key={subject.id}
                                          value={subject.id}
                                        >
                                          {subject.subjectName}
                                        </SelectItem>
                                      ))}
                                    </>
                                  )}
                                </SelectGroup>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Event Date */}
          <div className="grid gap-2 flex-1 mb-4">
            <label className="text-sm font-medium block text-gray-700">
              <CalendarIcon className="h-4 w-4 inline-block mr-1" />
              Event Date
            </label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant={'outline'}
                  className={cn(
                    'justify-start text-left font-normal',
                    !formData.eventDate && 'text-muted-foreground'
                  )}
                >
                  {formData.eventDate ? (
                    format(formData.eventDate, 'PPP')
                  ) : (
                    <span>Pick a date</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="w-auto p-0"
                align="start"
                side="bottom"
              >
                <Calendar
                  mode="single"
                  selected={formData.eventDate}
                  onSelect={(date) => {
                    if (date) {
                      const newDate = date;
                      newDate.setHours(formData.eventDate.getHours());
                      newDate.setMinutes(formData.eventDate.getMinutes());
                      setFormData({ ...formData, eventDate: newDate });
                    }
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className=" mb-4">
            <div className="grid gap-2 flex-1">
              <label className="text-sm font-medium mb-1.5 block text-gray-700">
                <Clock className="h-4 w-4 inline-block mr-1" />
                Time
              </label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">
                    Start
                  </label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        {format(formData.startTime, 'h:mm a')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-auto p-0"
                      align="start"
                      side="bottom"
                      sideOffset={4}
                    >
                      <div className="p-4" onClick={(e) => e.stopPropagation()}>
                        <div className="space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="text-xs mb-1 block">Hour</label>
                              <Select
                                value={format(formData.startTime, 'HH')}
                                onValueChange={(value) => {
                                  const newTime = new Date(formData.startTime);
                                  newTime.setHours(Number.parseInt(value));
                                  setFormData({
                                    ...formData,
                                    startTime: newTime,
                                  });
                                }}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent position="popper">
                                  {Array.from({ length: 24 }).map((_, i) => (
                                    <SelectItem
                                      key={i}
                                      value={i.toString().padStart(2, '0')}
                                    >
                                      {i === 0
                                        ? '12 AM'
                                        : i < 12
                                        ? `${i} AM`
                                        : i === 12
                                        ? '12 PM'
                                        : `${i - 12} PM`}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <label className="text-xs mb-1 block">
                                Minute
                              </label>
                              <Select
                                value={format(formData.startTime, 'mm')}
                                onValueChange={(value) => {
                                  const newTime = new Date(formData.startTime);
                                  newTime.setMinutes(Number.parseInt(value));
                                  setFormData({
                                    ...formData,
                                    startTime: newTime,
                                  });
                                }}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent position="popper">
                                  {Array.from({ length: 12 }).map((_, i) => (
                                    <SelectItem
                                      key={i}
                                      value={(i * 5)
                                        .toString()
                                        .padStart(2, '0')}
                                    >
                                      {(i * 5).toString().padStart(2, '0')}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">
                    End
                  </label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        {format(formData.endTime, 'h:mm a')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-auto p-0"
                      align="start"
                      side="bottom"
                      sideOffset={4}
                    >
                      <div className="p-4" onClick={(e) => e.stopPropagation()}>
                        <div className="space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="text-xs mb-1 block">Hour</label>
                              <Select
                                value={format(formData.endTime, 'HH')}
                                onValueChange={(value) => {
                                  const newTime = new Date(formData.endTime);
                                  newTime.setHours(Number.parseInt(value));
                                  setFormData({
                                    ...formData,
                                    endTime: newTime,
                                  });
                                }}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent position="popper">
                                  {Array.from({ length: 24 }).map((_, i) => (
                                    <SelectItem
                                      key={i}
                                      value={i.toString().padStart(2, '0')}
                                    >
                                      {i === 0
                                        ? '12 AM'
                                        : i < 12
                                        ? `${i} AM`
                                        : i === 12
                                        ? '12 PM'
                                        : `${i - 12} PM`}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <label className="text-xs mb-1 block">
                                Minute
                              </label>
                              <Select
                                value={format(formData.endTime, 'mm')}
                                onValueChange={(value) => {
                                  const newTime = new Date(formData.endTime);
                                  newTime.setMinutes(Number.parseInt(value));
                                  setFormData({
                                    ...formData,
                                    endTime: newTime,
                                  });
                                }}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent position="popper">
                                  {Array.from({ length: 12 }).map((_, i) => (
                                    <SelectItem
                                      key={i}
                                      value={(i * 5)
                                        .toString()
                                        .padStart(2, '0')}
                                    >
                                      {(i * 5).toString().padStart(2, '0')}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>
          </div>

          <div className="grid gap-2 flex-1 mb-4">
            <label className="text-sm font-medium mb-1.5 block text-gray-700">
              <MapPin className="h-4 w-4 inline-block mr-1" />
              Location
            </label>
            <Input
              placeholder="Add location"
              value={formData.location}
              onChange={(e) =>
                setFormData({ ...formData, location: e.target.value })
              }
              className="flex-1"
            />
          </div>

          {/* Participants */}
          <div className="mb-4">
            <div className="grid gap-2 flex-1">
              <label className="text-sm font-medium mb-1.5 block text-gray-700">
                <Users className="h-4 w-4 inline-block mr-1" />
                Participants
              </label>

              <div className="space-y-3">
                {/* Selected participants */}
                <div className="flex flex-wrap gap-2">
                  {formData.participants.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      No participants selected
                    </p>
                  ) : (
                    formData.participants.map((participant) => (
                      <Badge
                        key={participant.id}
                        variant="secondary"
                        className="flex items-center gap-1 pl-1 pr-1.5 py-1"
                      >
                        <Avatar className="h-5 w-5 mr-1">
                          <AvatarImage
                            src={participant.avatar || '/placeholder.svg'}
                            alt={participant.name}
                          />
                          <AvatarFallback>
                            {participant.name?.charAt(0) || '0'}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-xs">{participant.name}</span>
                        <button
                          type="button"
                          onClick={() => removeParticipant(participant.id)}
                          className="ml-1 rounded-full hover:bg-gray-200 p-0.5"
                        >
                          <X className="h-3 w-3" />
                          <span className="sr-only">Remove</span>
                        </button>
                      </Badge>
                    ))
                  )}
                </div>

                {/* Add participants button */}
                <Popover
                  open={isParticipantsOpen}
                  onOpenChange={setIsParticipantsOpen}
                >
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-1"
                    >
                      <Plus className="h-4 w-4" />
                      Add Participants
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-80 p-0"
                    align="start"
                    side="bottom"
                    sideOffset={4}
                  >
                    <div
                      className="p-4 border-b"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Input
                        placeholder="Search participants..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full"
                      />
                    </div>

                    <Tabs
                      defaultValue="students"
                      value={activeTab}
                      onValueChange={setActiveTab}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="border-b">
                        <TabsList className="w-full justify-start rounded-none border-b bg-transparent p-0">
                          <TabsTrigger
                            value="students"
                            className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#7C3AED] data-[state=active]:bg-transparent"
                          >
                            Students
                          </TabsTrigger>
                          <TabsTrigger
                            value="staff"
                            className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#7C3AED] data-[state=active]:bg-transparent"
                          >
                            Staff
                          </TabsTrigger>
                          <TabsTrigger
                            value="parents"
                            className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#7C3AED] data-[state=active]:bg-transparent"
                          >
                            Parents
                          </TabsTrigger>
                          <TabsTrigger
                            value="groups"
                            className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#7C3AED] data-[state=active]:bg-transparent"
                          >
                            Groups
                          </TabsTrigger>
                        </TabsList>
                      </div>

                      {isLoading ? (
                        <div>loading...</div>
                      ) : (
                        <div>
                          {isError ? (
                            <div>An Error Occurred</div>
                          ) : (
                            <div className="max-h-60 overflow-auto p-1">
                              {filteredParticipants.length === 0 ? (
                                <p className="text-center py-4 text-sm text-gray-500">
                                  No {activeTab} found
                                </p>
                              ) : activeTab === 'groups' ? (
                                filteredParticipants.map((participant) => {
                                  return (
                                    <div
                                      key={participant.name}
                                      className="flex items-center justify-between p-2 hover:bg-gray-100 rounded-md cursor-pointer"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        addParticipant(participant);
                                      }}
                                    >
                                      <div className="flex items-center gap-2">
                                        <div>
                                          <p className="text-sm font-medium">
                                            {participant.name}
                                          </p>
                                        </div>
                                      </div>
                                      <Checkbox
                                        checked={isGroupSubset(participant)}
                                        onCheckedChange={(checked) => {
                                          if (checked) {
                                            addParticipant(participant);
                                          } else {
                                            removeParticipantGroup(participant);
                                          }
                                        }}
                                        className="text-[#7C3AED] border-gray-300"
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                    </div>
                                  );
                                })
                              ) : (
                                filteredParticipants.map((participant) => (
                                  <div
                                    key={participant.id}
                                    className="flex items-center justify-between p-2 hover:bg-gray-100 rounded-md cursor-pointer"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      addParticipant(participant);
                                    }}
                                  >
                                    <div className="flex items-center gap-2">
                                      <Avatar className="h-8 w-8">
                                        <AvatarImage
                                          src={
                                            participant.avatar ||
                                            '/placeholder.svg'
                                          }
                                          alt={participant.name}
                                        />
                                        <AvatarFallback>
                                          {participant.name.charAt(0)}
                                        </AvatarFallback>
                                      </Avatar>
                                      <div>
                                        <p className="text-sm font-medium">
                                          {participant.name}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                          {participant.email}
                                        </p>
                                      </div>
                                    </div>
                                    <Checkbox
                                      checked={formData.participants.some(
                                        (p) => p.id === participant.id
                                      )}
                                      onCheckedChange={(checked) => {
                                        if (checked) {
                                          addParticipant(participant);
                                        } else {
                                          removeParticipant(participant.id);
                                        }
                                      }}
                                      className="text-[#7C3AED] border-gray-300"
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  </div>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </Tabs>

                    <div
                      className="flex justify-end gap-2 p-4 border-t"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsParticipantsOpen(false);
                        }}
                      >
                        Done
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>

          {/* Recurring switch and rule */}
          <div className="mb-4">
            <div className="flex-1 space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">
                  <Repeat className="h-4 w-4 inline-block mr-1" />
                  Recurring Event
                </label>
                <Switch
                  checked={formData.recurring}
                  onCheckedChange={(checked) => {
                    setFormData({
                      ...formData,
                      recurring: checked,
                      recurrenceRule: checked ? 'FREQ=WEEKLY' : '',
                    });
                  }}
                  className="data-[state=checked]:bg-[#7C3AED]"
                />
              </div>
              {formData.recurring && (
                <div className="pt-2">
                  <Select
                    value={formData.recurrenceRule}
                    onValueChange={(value) => {
                      return setFormData({
                        ...formData,
                        recurrenceRule: value,
                      });
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select recurrence pattern" />
                    </SelectTrigger>
                    <SelectContent>
                      {recurringOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="mb-4">
            <div className="grid gap-2 flex-1">
              <label className="text-sm font-medium mb-1.5 block text-gray-700">
                <ScrollText className="h-4 w-4 inline-block mr-1" />
                Description
              </label>
              <Textarea
                placeholder="Add description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                className="flex-1 resize-none"
                rows={3}
              />
            </div>
          </div>

          <div className="flex items-center justify-between border-t p-4 mb-4">
            <div className="flex items-center">
              {event && (
                <Button
                  type="button"
                  variant="ghost"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => {
                    onDelete?.(event.id);
                    onClose();
                  }}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              )}
            </div>
            <div className="flex space-x-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-[#7C3AED] hover:bg-[#7C3AED]/90 text-white"
              >
                {event ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EventModal;
